import { useState } from "react";
import axios from "axios";
import { CheckCircle2, IndianRupee, Loader2, Search, School, UserRound } from "lucide-react";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import SchoolHeader from "../components/SchoolHeader";
import { states } from "@/options/states";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";
const EXAMS = [
  { code: "NSTSE", name: "National Level Science Talent Search Examination", selected: "isNSTSESelected", date: "nstseExamDate" },
  { code: "UIMO", name: "Unified International Mathematics Olympiad", selected: "isUIMOSelected", date: "uimoExamDate" },
  { code: "UIEO", name: "Unified International English Olympiad", selected: "isUIEOSelected", date: "uieoExamDate" },
  { code: "UCO", name: "Unified International Cyber Olympiad", selected: "isUCOSelected", date: "ucoExamDate" },
  { code: "UIGKO", name: "Unified International General Knowledge Olympiad", selected: "isUIGKOSelected", date: "uigkoExamDate" },
];
const OPTION_TEXT = {
  1: "Exam fee",
  2: "Exam fee + previous question papers and revision material (E-Book)",
  3: "Option 2 features + online chapter-wise tests and previous question papers",
};
const EMPTY_STUDENT = {
  className: "", section: "", studentName: "", parentName: "", mobile: "", email: "",
  addressLine1: "", addressLine2: "", city: "", state: "Andhra Pradesh", pincode: "",
};


const StudentRegistrationThroughtSchool = () => {
  const [schoolId, setSchoolId] = useState("");
  const [school, setSchool] = useState(null);
  const [prices, setPrices] = useState([]);
  const [selections, setSelections] = useState({});
  const [student, setStudent] = useState(EMPTY_STUDENT);
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const clearSchool = () => {
    setSchool(null);
    setPrices([]);
    setSelections({});
  };

  const findSchool = async (event) => {
    event.preventDefault();
    clearSchool();
    const normalizedId = schoolId.trim();
    if (!/^\d+$/.test(normalizedId)) return toast.error("Enter a valid numeric School ID");
    setIsSearching(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/school-registration/${normalizedId}`);
      setSchool(response.data?.data?.school || null);
      setPrices(Array.isArray(response.data?.data?.prices) ? response.data.data.prices : []);
      toast.success("School details found");
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to find school details");
    } finally { setIsSearching(false); }
  };

  const availableExams = school ? EXAMS.filter((exam) => Boolean(school[exam.selected])) : [];
  const getOptions = (examCode) => prices.filter((row) => row.examCode === examCode);
  const updateStudent = (event) => setStudent((current) => ({ ...current, [event.target.name]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!student.className || !student.section.trim()) return toast.error("Select class and enter the section");
    if (availableExams.length && Object.keys(selections).length === 0) return toast.error("Select at least one Olympiad option");
    if (!student.studentName.trim() || !student.parentName.trim() || !/^\d{10}$/.test(student.mobile) || !student.email.trim()) {
      return toast.error("Complete all student details with a valid 10-digit mobile number");
    }
    if (!student.addressLine1.trim() || !student.addressLine2.trim() || !student.city.trim() || !student.state || !student.pincode.trim()) {
      return toast.error("Complete all student address details");
    }
    if (student.addressLine1.includes("#") || student.addressLine2.includes("#")) return toast.error("Do not enter the # symbol in address fields");
    if (!/^\d{6}$/.test(student.pincode)) return toast.error("Enter a valid 6-digit PIN code");
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/student-registration-through-school/${school.schoolId}`, {
        ...student,
        selections,
      });
      toast.success(`Registration saved${response.data?.data?.registrationId ? ` (ID: ${response.data.data.registrationId})` : ""}`);
      setStudent(EMPTY_STUDENT);
      setSelections({});
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to save student registration");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SchoolHeader />
      <main className="mx-auto max-w-6xl space-y-5 px-4 py-7 sm:px-6">
        <section className="rounded-3xl border border-slate-200 bg-white p-5 text-left shadow-sm sm:p-7">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-sky-600">Student registration</p>
          <h1 className="mt-1 text-2xl font-black text-slate-900 sm:text-3xl">Register through your school</h1>
          <p className="mt-2 text-sm text-slate-600">Enter the School ID provided by your school to view its details and available Olympiads.</p>

          <form onSubmit={findSchool} className="mt-5 flex max-w-xl flex-col gap-2 sm:flex-row">
            <div className="flex-1">
              <Label htmlFor="school-id">School ID</Label>
              <Input id="school-id" value={schoolId} onChange={(event) => { setSchoolId(event.target.value.replace(/\D/g, "")); clearSchool(); }} placeholder="Enter School ID" className="mt-1" />
            </div>
            <Button type="submit" className="sm:mt-6" disabled={isSearching || !schoolId}>
              {isSearching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}{isSearching ? "Checking..." : "Check school"}
            </Button>
          </form>
        </section>

        {school && (
          <form onSubmit={handleSubmit} className="space-y-5">
            <section className="rounded-3xl border border-emerald-200 bg-white p-5 text-left shadow-sm sm:p-6">
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-emerald-100 p-2.5 text-emerald-700"><School className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2"><h2 className="text-xl font-bold text-slate-900">{school.schoolName}</h2><span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-700"><CheckCircle2 className="mr-1 inline h-3 w-3" />Verified</span></div>
                  <p className="mt-1 text-sm text-slate-600">{[school.schoolAddress, school.place, school.district, school.state, school.country].filter(Boolean).join(", ")}</p>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold text-slate-600"><span className="rounded-lg bg-slate-100 px-2 py-1">School ID: {school.schoolId}</span>{school.regNo && <span className="rounded-lg bg-slate-100 px-2 py-1">USRN: {school.regNo}</span>}{school.examMode && <span className="rounded-lg bg-slate-100 px-2 py-1">Mode: {school.examMode}</span>}</div>
                </div>
              </div>
            </section>

            <section className="rounded-3xl border border-slate-200 bg-white p-5 text-left shadow-sm sm:p-6">
              <h2 className="text-lg font-bold text-slate-900">Class details</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div><Label htmlFor="className">Class</Label><select id="className" name="className" value={student.className} onChange={updateStudent} className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"><option value="">Select class</option>{Array.from({ length: 10 }, (_, index) => <option key={index + 1} value={index + 1}>Class {index + 1}</option>)}</select></div>
                <div><Label htmlFor="section">Section</Label><Input id="section" name="section" value={student.section} onChange={updateStudent} placeholder="Enter NA if not known" className="mt-1" /></div>
              </div>
            </section>

            <section className="rounded-3xl border border-slate-200 bg-white p-5 text-left shadow-sm sm:p-6">
              <h2 className="text-lg font-bold text-slate-900">Available Olympiad options</h2>
              <p className="mt-1 text-sm text-slate-600">Only Olympiads selected by your school are shown.</p>
              {availableExams.length === 0 ? <div className="mt-4 rounded-xl bg-amber-50 p-4 text-sm text-amber-800">This school has not selected any Olympiads yet.</div> : (
                <div className="mt-4 space-y-4">{availableExams.map((exam) => (
                  <article key={exam.code} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-2"><div><h3 className="font-bold text-slate-900">{exam.name}</h3><p className="text-xs font-semibold text-sky-700">{exam.code}</p></div>{school[exam.date] && <span className="rounded-lg bg-white px-2 py-1 text-xs font-semibold text-slate-600">Exam: {school[exam.date]}</span>}</div>
                    <div className="mt-3 grid gap-2 md:grid-cols-3">{getOptions(exam.code).map((option) => {
                      const selected = selections[exam.code] === option.optionId;
                      return <label key={option.optionId} className={`cursor-pointer rounded-xl border p-3 transition ${selected ? "border-sky-500 bg-sky-50 ring-1 ring-sky-500" : "border-slate-200 bg-white hover:border-sky-300"}`}><div className="flex items-center justify-between gap-2"><span className="flex items-center gap-2 font-bold text-slate-800"><input type="radio" name={exam.code} checked={selected} onChange={() => setSelections((current) => ({ ...current, [exam.code]: option.optionId }))} />Option {option.optionId}</span><span className="flex items-center font-black text-slate-900"><IndianRupee className="h-3.5 w-3.5" />{option.discountedPrice}</span></div><p className="mt-2 text-xs leading-5 text-slate-600">{OPTION_TEXT[option.optionId]}</p></label>;
                    })}</div>
                  </article>
                ))}</div>
              )}
            </section>

            <section className="rounded-3xl border border-slate-200 bg-white p-5 text-left shadow-sm sm:p-6">
              <div className="flex items-center gap-2"><UserRound className="h-5 w-5 text-sky-600" /><h2 className="text-lg font-bold text-slate-900">Student details</h2></div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {[["studentName", "Student name", "Enter student's full name", "text"], ["parentName", "Father's / Mother's name", "Enter parent name", "text"], ["mobile", "WhatsApp mobile number", "10-digit mobile number", "tel"], ["email", "Email address", "Enter a valid email", "email"]].map(([name, label, placeholder, type]) => <div key={name}><Label htmlFor={name}>{label}</Label><Input id={name} name={name} type={type} value={student[name]} onChange={updateStudent} placeholder={placeholder} className="mt-1" required /></div>)}
                <div><Label htmlFor="addressLine1">Address Line 1</Label><Textarea id="addressLine1" name="addressLine1" value={student.addressLine1} onChange={updateStudent} placeholder="Do not enter # symbol" maxLength={50} className="mt-1 min-h-24 resize-y" required /></div>
                <div><Label htmlFor="addressLine2">Address Line 2</Label><Textarea id="addressLine2" name="addressLine2" value={student.addressLine2} onChange={updateStudent} placeholder="Do not enter # symbol" maxLength={50} className="mt-1 min-h-24 resize-y" required /></div>
                <div><Label htmlFor="city">City</Label><Input id="city" name="city" value={student.city} onChange={updateStudent} placeholder="Enter city" className="mt-1" required /></div>
                <div><Label htmlFor="state">State</Label><select id="state" name="state" value={student.state} onChange={updateStudent} className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" required>{states.map((state) => <option key={state} value={state}>{state}</option>)}</select></div>
                <div><Label htmlFor="pincode">PIN code / ZIP code</Label><Input id="pincode" name="pincode" inputMode="numeric" value={student.pincode} onChange={(event) => setStudent((current) => ({ ...current, pincode: event.target.value.replace(/\D/g, "").slice(0, 6) }))} placeholder="Enter 6-digit PIN code" className="mt-1" required /></div>
              </div>
              <div className="mt-5 flex justify-end"><Button type="submit" disabled={availableExams.length === 0 || isSubmitting}>{isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{isSubmitting ? "Saving..." : "Submit registration"}</Button></div>
            </section>
          </form>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default StudentRegistrationThroughtSchool;
